import { Component, OnInit, QueryList, signal, ViewChildren } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { NbDialogService, NbToastrService } from '@nebular/theme';
import { HttpClient } from '@angular/common/http';
import {
  CustomFieldCollectionDetailDto,
  CustomFieldCollectionInstanceDetailDto,
  CustomFieldCollectionInstanceMergeDto,
  CustomFieldCollectionService,
  ProtocolCommentUpdateDto,
  ProtocolDetailDto,
  ProtocolMergeDto,
  ProtocolService,
  UserDetailDto,
} from '@artemis-software/wr-api';
import { environment } from '../../../environments/environment';
import { CustomFormComponent } from '@shared/custom-form/custom-form/custom-form.component';
import { validateCustomForms } from '@shared/custom-form/custom-form-submit';
import { DeleteDialogComponent } from '@components/dialog/delete-dialog/delete-dialog.component';
import { ProtocolPublishDialogComponent } from '@components/dialog/protocol-publish-dialog/protocol-publish-dialog.component';
import { ProtocolShareLinkDialogComponent } from '@components/dialog/protocol-share-link-dialog/protocol-share-link-dialog.component';
import { PendingChanges } from '@guards/pending-changes.guard';

export interface ProtocolSection {
  instance: CustomFieldCollectionInstanceDetailDto;
  collectionDetail: CustomFieldCollectionDetailDto;
  expanded: boolean;
}

@Component({
  standalone: false,
  selector: 'wr-protocol-detail-page',
  templateUrl: './protocol-detail-page.component.html',
  styleUrls: ['./protocol-detail-page.component.scss'],
})
export class ProtocolDetailPageComponent implements OnInit, PendingChanges {

  @ViewChildren(CustomFormComponent)
  formComponents!: QueryList<CustomFormComponent>;

  readonly saving$ = new BehaviorSubject(false);
  readonly publishing$ = new BehaviorSubject(false);
  readonly loading$ = new BehaviorSubject(true);
  readonly isEditing = signal(false);
  readonly canEdit = signal(false);
  readonly canPublish = signal(false);
  readonly canDelete = signal(false);
  readonly sections = signal<ProtocolSection[]>([]);

  protocol?: ProtocolDetailDto;
  protocolId: string | null = null;
  commentsForm!: FormGroup;

  readonly pdfDownloading$ = new BehaviorSubject(false);

  constructor(
    private readonly location: Location,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly protocolService: ProtocolService,
    private readonly collectionService: CustomFieldCollectionService,
    private readonly dialogService: NbDialogService,
    private readonly toastrService: NbToastrService,
    private readonly fb: FormBuilder,
    private readonly http: HttpClient,
  ) {}

  ngOnInit(): void {
    this.route.paramMap.subscribe(params => {
      this.protocolId = params.get('id');
      if (this.protocolId) {
        this.loadProtocol(this.protocolId);
      }
    });
  }

  async loadProtocol(id: string): Promise<void> {
    try {
      this.loading$.next(true);
      const protocol = await firstValueFrom(this.protocolService.findById(id));
      this.protocol = protocol;
      this.canEdit.set(protocol.permissions.canEdit);
      this.canPublish.set(protocol.permissions.canPublish);
      this.canDelete.set(protocol.permissions.canDelete);
      this.buildCommentsForm(protocol);

      const sections = await Promise.all(
        protocol.instances.map(async instance => {
          const collectionDetail = await firstValueFrom(
            this.collectionService.findById(instance.collection.id),
          );
          return {
            instance,
            collectionDetail,
            expanded: false,
          } as ProtocolSection;
        }),
      );
      this.sections.set(sections);
    } catch {
      this.toastrService.danger('Protokoll konnte nicht geladen werden.', 'Fehler');
    } finally {
      this.loading$.next(false);
    }
  }

  buildCommentsForm(protocol: ProtocolDetailDto): void {
    this.commentsForm = this.fb.group({
      comments: this.fb.array(
        (protocol.comments ?? []).map(c =>
          this.fb.group({
            id: new FormControl(c.id),
            text: new FormControl(c.text),
            isChecked: new FormControl(c.isChecked),
            createdBy: new FormControl(c.createdBy),
            checkedBy: new FormControl(c.checkedBy),
            created: new FormControl(c.created),
            lastUpdated: new FormControl(c.lastUpdated),
          }),
        ),
      ),
    });
    if (!this.isEditing()) {
      this.comments.disable();
    }
  }

  get comments(): FormArray {
    return this.commentsForm.get('comments') as FormArray;
  }

  toggleExpand(index: number): void {
    this.sections.update(sections =>
      sections.map((s, i) => i === index ? { ...s, expanded: !s.expanded } : s),
    );
  }

  expandAll(): void {
    this.sections.update(sections => sections.map(s => ({ ...s, expanded: true })));
  }

  collapseAll(): void {
    this.sections.update(sections => sections.map(s => ({ ...s, expanded: false })));
  }

  onEdit(): void {
    this.isEditing.set(true);
    this.comments.enable();
  }

  async cancelEdit(): Promise<void> {
    this.isEditing.set(false);
    if (this.protocolId) {
      await this.loadProtocol(this.protocolId);
    }
  }

  async submit(): Promise<void> {
    const forms = this.formComponents.toArray();
    const validation = validateCustomForms(forms);
    if (validation.status === 'not-ready') {
      this.toastrService.danger('Formular ist noch nicht bereit.', 'Fehler');
      return;
    }
    if (validation.status === 'invalid') {
      this.toastrService.danger(validation.message, validation.title);
      return;
    }
    try {
      this.saving$.next(true);
      const instances: CustomFieldCollectionInstanceMergeDto[] = forms.map(form => form.getMergeDto());
      const comments: ProtocolCommentUpdateDto[] = this.comments.controls
        .filter(ctrl => (ctrl.get('text')?.value ?? '').trim().length > 0)
        .map(ctrl => ({
          id: ctrl.get('id')?.value ?? undefined,
          text: ctrl.get('text')?.value ?? '',
          isChecked: ctrl.get('isChecked')?.value ?? false,
        }));
      const mergeDto: ProtocolMergeDto = {
        id: this.protocolId ?? undefined,
        instances,
        comments,
      };
      await firstValueFrom(this.protocolService.merge(mergeDto));
      this.isEditing.set(false);
      this.toastrService.success('Protokoll gespeichert', 'Erfolg');
      if (this.protocolId) {
        await this.loadProtocol(this.protocolId);
      }
    } catch {
      this.toastrService.danger('Protokoll konnte nicht gespeichert werden.', 'Fehler');
    } finally {
      this.saving$.next(false);
    }
  }

  async publishProtocol(): Promise<void> {
    if (!this.protocolId) return;
    const confirmed = await firstValueFrom(
      this.dialogService.open(ProtocolPublishDialogComponent, {
        context: { protocolId: this.protocolId },
      }).onClose,
    );
    if (!confirmed) return;
    this.publishing$.next(true);
    try {
      await firstValueFrom(this.protocolService.merge({ id: this.protocolId, status: 'PUBLISHED' } as any));
      this.toastrService.success('Protokoll veröffentlicht', 'Erfolg');
      await this.loadProtocol(this.protocolId);
    } catch {
      this.toastrService.danger('Protokoll konnte nicht veröffentlicht werden.', 'Fehler');
    } finally {
      this.publishing$.next(false);
    }
  }

  async unpublishProtocol(): Promise<void> {
    if (!this.protocolId) return;
    this.publishing$.next(true);
    try {
      await firstValueFrom(this.protocolService.merge({ id: this.protocolId, status: 'DRAFT' } as any));
      this.toastrService.success('Protokoll als Entwurf zurückgezogen', 'Erfolg');
      await this.loadProtocol(this.protocolId);
    } catch {
      this.toastrService.danger('Protokoll konnte nicht zurückgezogen werden.', 'Fehler');
    } finally {
      this.publishing$.next(false);
    }
  }

  onDelete(): void {
    this.dialogService.open(DeleteDialogComponent, {
      context: {
        title: 'Protokoll löschen',
        message: 'Möchten Sie dieses Protokoll wirklich löschen?',
      },
    }).onClose.subscribe(async (confirmed) => {
      if (confirmed && this.protocolId) {
        await firstValueFrom(this.protocolService.deleteById(this.protocolId));
        this.toastrService.success('Protokoll gelöscht', 'Erfolg');
        this.router.navigate(['protocols']);
      }
    });
  }

  async downloadPdf(): Promise<void> {
    if (!this.protocolId || !this.protocol) return;
    this.pdfDownloading$.next(true);
    try {
      const blob = await firstValueFrom(
        this.http.get(`${environment.apiUrl}/api/protocol/${this.protocolId}/exportPdf`, {
          responseType: 'blob',
        }),
      );
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `protokoll-${this.protocol.protocolType.shortcut}-${this.protocolId}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch {
      this.toastrService.danger('PDF konnte nicht erstellt werden.', 'Fehler');
    } finally {
      this.pdfDownloading$.next(false);
    }
  }

  openShareLinkDialog(): void {
    if (!this.protocolId || !this.protocol) return;
    const ref = this.dialogService.open(ProtocolShareLinkDialogComponent, {
      context: {
        protocolId: this.protocolId,
        existingShareLinkId: this.protocol.shareLinkId ?? undefined,
        existingShareLinkExpiration: this.protocol.shareLinkExpiration ?? undefined,
      },
    });
    ref.onClose.subscribe(() => {
      const dialog = ref.componentRef?.instance;
      if (!dialog || !this.protocol) return;
      this.protocol = {
        ...this.protocol,
        shareLinkId: dialog.shareLinkId(),
        shareLinkExpiration: dialog.shareLinkExpiration(),
      };
    });
  }

  navigateBack(): void {
    this.location.back();
  }

  hasPendingChanges(): boolean {
    return this.isEditing();
  }

  creatorName(protocol: ProtocolDetailDto): string {
    if (protocol.creator) {
      return `${protocol.creator.firstName} ${protocol.creator.lastName}`;
    }
    return protocol.creatorInfo.name ?? '';
  }

  getUserProfile(user: UserDetailDto | undefined): string {
    const fallback = 'https://i.imgur.com/ujW6tKI.png';
    return user?.userProfilePicture?.presignedUrl ?? fallback;
  }

  typeBadgeStyle(color: string | undefined): object {
    if (!color) return {};

    return {
      background: color + '22',
      border: `1.5px solid ${color}`,
      color: color,
    };
  }

  getHeaderBg(color: string | undefined): string {
    if (!color || color.length < 7) return '';
    const hex = color.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, 0.12)`;
  }

  hasRequiredFields(section: ProtocolSection): boolean {
    return section.collectionDetail.members?.some(m => m.required) ?? false;
  }

}
